import { jwtDecode } from 'jwt-decode'
import { FT_TENANT_CLAIM } from 'common/c'

export type LogLinkType = 'trx' | 'ubj'

export type LogLinkSource =
  | {
      type: 'trx'
      hardwareId: string
      instanceId: number
      recordNumbers: string[]
    }
  | { type: 'ubj'; id: string; year: number }

export type LogLink = {
  tenantId: string
  startLine: number
  endLine: number
  source: LogLinkSource
}

export const LOG_LINK_PATHS: Record<LogLinkType, string> = {
  trx: '/admin/trx-stream',
  ubj: '/admin/ubj-uploads',
}

export const LOG_LINK_PARAMS = {
  tenantId: 'tenantId',
  startLine: 'startLine',
  endLine: 'endLine',
  serialNumber: 'serialNumber',
  instanceId: 'instanceId',
  recordNumbers: 'recordNumbers',
  ubjId: 'ubjId',
  year: 'year',
} as const

const UNSIGNED_INTEGER = /^\d+$/
const RECORD_SEPARATOR = ','

const parsePositiveLine = (value: string | null) =>
  value !== null && UNSIGNED_INTEGER.test(value) && Number(value) > 0
    ? Number(value)
    : undefined

export const buildLogLink = (origin: string, link: LogLink): string => {
  const params = new URLSearchParams({
    [LOG_LINK_PARAMS.tenantId]: link.tenantId,
    [LOG_LINK_PARAMS.startLine]: String(link.startLine),
    [LOG_LINK_PARAMS.endLine]: String(link.endLine),
  })
  if (link.source.type === 'trx') {
    params.set(LOG_LINK_PARAMS.serialNumber, link.source.hardwareId)
    params.set(LOG_LINK_PARAMS.instanceId, String(link.source.instanceId))
    params.set(
      LOG_LINK_PARAMS.recordNumbers,
      link.source.recordNumbers.join(RECORD_SEPARATOR),
    )
  } else {
    params.set(LOG_LINK_PARAMS.ubjId, link.source.id)
    params.set(LOG_LINK_PARAMS.year, String(link.source.year))
  }
  return `${origin}${LOG_LINK_PATHS[link.source.type]}?${params.toString()}`
}

export const parseLogLink = (
  params: URLSearchParams,
  type: LogLinkType,
): LogLink | null => {
  const tenantId = params.get(LOG_LINK_PARAMS.tenantId)?.trim()
  const startLine = parsePositiveLine(params.get(LOG_LINK_PARAMS.startLine))
  const endLine =
    params.get(LOG_LINK_PARAMS.endLine) === null
      ? startLine
      : parsePositiveLine(params.get(LOG_LINK_PARAMS.endLine))
  if (!tenantId || startLine === undefined || endLine === undefined) {
    return null
  }

  if (type === 'trx') {
    const hardwareId = params.get(LOG_LINK_PARAMS.serialNumber)?.trim()
    const instanceId = params.get(LOG_LINK_PARAMS.instanceId)
    // Record numbers are BIGINTs, so they stay strings to avoid precision loss.
    const recordNumbers =
      params.get(LOG_LINK_PARAMS.recordNumbers)?.split(RECORD_SEPARATOR) ?? []
    if (
      !hardwareId ||
      instanceId === null ||
      !UNSIGNED_INTEGER.test(instanceId) ||
      recordNumbers.length === 0 ||
      !recordNumbers.every((record) => UNSIGNED_INTEGER.test(record))
    ) {
      return null
    }
    return {
      tenantId,
      startLine,
      endLine,
      source: {
        type,
        hardwareId,
        instanceId: Number(instanceId),
        recordNumbers,
      },
    }
  }

  const id = params.get(LOG_LINK_PARAMS.ubjId)
  const year = params.get(LOG_LINK_PARAMS.year)
  if (
    id === null ||
    !UNSIGNED_INTEGER.test(id) ||
    year === null ||
    !UNSIGNED_INTEGER.test(year)
  ) {
    return null
  }
  return {
    tenantId,
    startLine,
    endLine,
    source: { type, id, year: Number(year) },
  }
}

export const removeLogLinkParams = (params: URLSearchParams) => {
  const remaining = new URLSearchParams(params)
  Object.values(LOG_LINK_PARAMS).forEach((name) => remaining.delete(name))
  return remaining
}

export const getTenantIdFromToken = (token?: string): string | undefined => {
  if (!token) return undefined
  try {
    const claim = jwtDecode<Record<string, unknown>>(token)[FT_TENANT_CLAIM]
    return typeof claim === 'string' ? claim : undefined
  } catch {
    return undefined
  }
}

export type LogLinkAction = 'none' | 'open' | 'switch'

export type SelectableLogLine = {
  recordNumber: number | string
  lineNumber: number
  instanceId?: number
}

export type LineSelection = { anchor: string; focus: string }

export const getLineKey = (recordNumber: number | string, lineNumber: number) =>
  `${recordNumber}:${lineNumber}`

// Lines are in display order, so a selection may run across several records.
export const getSelectedLines = <Line extends SelectableLogLine>(
  lines: Line[],
  selection: LineSelection | null,
): Line[] => {
  if (!selection) return []
  const indexOf = (key: string) =>
    lines.findIndex(
      (line) => getLineKey(line.recordNumber, line.lineNumber) === key,
    )
  const anchorIndex = indexOf(selection.anchor)
  if (anchorIndex < 0) return []
  const focusIndex = indexOf(selection.focus)
  const endIndex = focusIndex < 0 ? anchorIndex : focusIndex
  return lines.slice(
    Math.min(anchorIndex, endIndex),
    Math.max(anchorIndex, endIndex) + 1,
  )
}

export const getLogLinkForSelection = ({
  tenantId,
  selectedLines,
  file,
  hardwareId,
  instanceId,
}: {
  tenantId?: string
  selectedLines: SelectableLogLine[]
  file?: { id: number | string; year: number }
  hardwareId?: string | null
  instanceId?: number
}): LogLink | null => {
  if (!tenantId || selectedLines.length === 0) return null
  const range = {
    tenantId,
    startLine: selectedLines[0].lineNumber,
    endLine: selectedLines[selectedLines.length - 1].lineNumber,
  }
  if (file) {
    return {
      ...range,
      source: { type: 'ubj', id: String(file.id), year: file.year },
    }
  }
  const instanceIds = new Set(
    selectedLines.map((line) => line.instanceId ?? instanceId),
  )
  const [lineInstanceId] = instanceIds
  if (!hardwareId || instanceIds.size !== 1 || lineInstanceId === undefined) {
    return null
  }
  return {
    ...range,
    source: {
      type: 'trx',
      hardwareId,
      instanceId: lineInstanceId,
      recordNumbers: [
        ...new Set(selectedLines.map((line) => String(line.recordNumber))),
      ],
    },
  }
}

export const getLogLinkAction = ({
  link,
  isAdmin,
  currentTenantId,
  switchAlreadyAttempted,
}: {
  link: LogLink | null
  isAdmin: boolean
  currentTenantId?: string
  switchAlreadyAttempted: boolean
}): LogLinkAction => {
  if (!link || !isAdmin) return 'none'
  if (link.tenantId === currentTenantId) return 'open'
  // A switch that already reloaded without reaching the tenant must not loop.
  return switchAlreadyAttempted ? 'none' : 'switch'
}
